import React, { useEffect, useMemo, useState } from 'react';
import { ChartBarIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { usePonds, useMasterData } from '../../hooks/useApi';
import apiClient from '../../utils/api';

const MedicineTrends: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [start, setStart] = useState<string>('');
  const [end, setEnd] = useState<string>('');
  const [selectedPondId, setSelectedPondId] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [rows, setRows] = useState<any[]>([]);

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const pondOptions = useMemo(() => ponds || [], [ponds]);
  const { data: medTypes } = useMasterData('medicine_types', selectedLocation?.id || null);
  const typeOptions = useMemo(() => medTypes || [], [medTypes]);

  useEffect(() => {
    const today = new Date();
    const to = today.toISOString().slice(0, 10);
    const from = new Date(today.getTime() - 29 * 86400000).toISOString().slice(0, 10);
    setStart(from);
    setEnd(to);
  }, []);

  const fetchData = async () => {
    if (!selectedLocation || !start || !end) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/reports/medicine-trends', {
        params: {
          location_id: selectedLocation.id,
          start,
          end,
          pond_id: selectedPondId || undefined,
          type: selectedType || undefined,
        },
      });
      setRows(res.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load medicine trends');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    if (!selectedLocation || !start || !end) return;
    try {
      const res = await apiClient.get(`/reports/export/medicine-trends`, {
        params: {
          location_id: selectedLocation.id,
          start,
          end,
          pond_id: selectedPondId || undefined,
          type: selectedType || undefined,
        },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `medicine_trends_${start}_${end}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {}
  };

  return (
    <div className="container-mobile py-6 space-y-6">
      <div className="flex items-center gap-2">
        <ChartBarIcon className="h-6 w-6 text-purple-600" />
        <h1 className="text-2xl font-bold text-gray-900">Medicine Usage Trends</h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-full px-3 py-2 border rounded" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-full px-3 py-2 border rounded" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pond</label>
            <select value={selectedPondId} onChange={(e) => setSelectedPondId(e.target.value)} className="w-full px-3 py-2 border rounded">
              <option value="">All ponds</option>
              {pondOptions.map((p: any) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Medicine Type</label>
            <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className="w-full px-3 py-2 border rounded">
              <option value="">All</option>
              {typeOptions.map((t: any) => (
                <option key={t.id} value={t.name}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">Apply Filters</button>
          <button onClick={downloadCsv} className="flex items-center px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700">
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" /> CSV
          </button>
        </div>

        {error && (
          <div className="mt-4 text-sm text-red-600">{error}</div>
        )}

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Pond</th>
                <th className="text-left px-3 py-2">Total Quantity</th>
                <th className="text-left px-3 py-2">Breakdown (by type)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-3 py-3">Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-3 text-gray-500">No data for selected range</td></tr>
              ) : (
                rows.map((r: any, idx: number) => (
                  <tr key={idx} className="border-t">
                    <td className="px-3 py-2">{r.date}</td>
                    <td className="px-3 py-2">{r.pond_name}</td>
                    <td className="px-3 py-2">{r.total_quantity}</td>
                    <td className="px-3 py-2">{
                      r.type_breakdown ? Object.entries(r.type_breakdown).map(([k,v]: any, i) => (
                        <span key={i} className="inline-block mr-2">{k}: {v as any}</span>
                      )) : '—'
                    }</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MedicineTrends;


